import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Plus, Buildings, MapPin, Phone, Envelope, ArrowRight,
    PencilSimple, MagnifyingGlass, Globe,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import RoleGuard from "../components/RoleGuard";
import OwnershipToggle from "../components/OwnershipToggle";
import OwnerCell from "../components/OwnerCell";
import ReassignOwnerDialog from "../components/ReassignOwnerDialog";
import EditBillingDetailsDialog from "../components/EditBillingDetailsDialog";
import CopyCodeButton from "../components/CopyCodeButton";
import {
    ShowArchivedToggle, ArchivedBadge, ArchiveRowButton, UnarchiveRowButton, useArchiveAction,
} from "../components/ArchiveControls";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function Clients() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    // Iter 51.2 — Archive UI is locked to the PRIMARY super-admin only
    // (server returns is_primary_admin from /auth/me + /auth/login).
    // Non-primary super_admins keep their other privileges (Reassign, etc.).
    const isPrimaryAdmin = !!user?.is_primary_admin;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editTarget, setEditTarget] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [reassignTarget, setReassignTarget] = useState(null);
    const [textQuery, setTextQuery] = useState("");
    const [mine, setMine] = useState(false);
    // Iter 51.1 — soft-delete (Archive) state
    const [showArchived, setShowArchived] = useState(false);
    const archive = useArchiveAction({
        entityType: "Client",
        archiveUrl: (row) => `/billing/client/${row.id}/archive`,
        unarchiveUrl: (row) => `/billing/client/${row.id}/unarchive`,
        onDone: () => load(),
        describe: (row) => (
            <>
                <div><span className="reedos-eyebrow">Client</span>{" "}
                    <span className="font-mono text-slate-900">{row.name}</span></div>
                {row.client_code && (
                    <div className="mt-1"><span className="reedos-eyebrow">Code</span>{" "}
                        <span className="text-slate-800 font-mono">{row.client_code}</span></div>
                )}
            </>
        ),
    });

    const visibleItems = useMemo(() => {
        const q = textQuery.trim().toLowerCase();
        if (!q) return items;
        return items.filter((c) => {
            const code = (c.client_code || "").toLowerCase();
            const name = (c.name || "").toLowerCase();
            const gstin = (c.gstin || "").toLowerCase();
            const contact = (c.contact_name || "").toLowerCase();
            const email = (c.contact_email || "").toLowerCase();
            const phone = (c.contact_phone || "").toLowerCase();
            const segment = (c.segment || "").toLowerCase();
            return (
                code.includes(q) || name.includes(q) || gstin.includes(q) || contact.includes(q) ||
                email.includes(q) || phone.includes(q) || segment.includes(q)
            );
        });
    }, [items, textQuery]);

    const load = async () => {
        try {
            const { data } = await api.get("/billing/client/list", {
                params: { mine, ...(showArchived ? { include_archived: true } : {}) },
            });
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load clients"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [mine, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div data-testid="clients-page">
            <PageHeader
                eyebrow="Master data"
                title="Clients"
                description="Companies you bill. GSTIN and state determine GST treatment on invoices."
                actions={
                    <RoleGuard permission="client:write">
                        <Button onClick={() => setCreateOpen(true)} data-testid="new-client-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                            <Plus size={16} weight="bold" /> New client
                        </Button>
                    </RoleGuard>
                }
            />

            <div className="px-6 sm:px-8 py-8">
                {!loading && (
                    <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="clients-search-bar">
                        <div className="relative flex-1 min-w-[260px] max-w-md">
                            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={textQuery}
                                onChange={(e) => setTextQuery(e.target.value)}
                                placeholder="Search by code, name, GSTIN, contact, email, phone or segment…"
                                className="pl-8 h-9 text-sm"
                                data-testid="clients-search-input"
                            />
                        </div>
                        <OwnershipToggle value={mine} onChange={setMine} testId="clients-ownership-toggle" />
                        {isPrimaryAdmin && (
                            <ShowArchivedToggle
                                value={showArchived}
                                onChange={setShowArchived}
                                count={items.filter((c) => c.deleted_at).length}
                                testid="clients-show-archived"
                            />
                        )}
                        {textQuery && (
                            <span className="text-xs text-slate-500" data-testid="clients-search-count">
                                {visibleItems.length} of {items.length}
                            </span>
                        )}
                    </div>
                )}
                {loading ? (
                    <div className="text-sm text-slate-500" data-testid="clients-loading">Loading clients...</div>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={<Buildings size={40} weight="thin" />}
                        title="No clients yet"
                        description="Add your first client to start raising Purchase Orders and Invoices."
                        data-testid="clients-empty"
                    />
                ) : visibleItems.length === 0 ? (
                    <EmptyState
                        icon={<MagnifyingGlass size={40} weight="thin" />}
                        title="No matching clients"
                        description="Try a different search term or clear the filter."
                        data-testid="clients-no-match"
                    />
                ) : (
                    <div className="reedos-card overflow-hidden" data-testid="clients-table-wrapper">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Company</th>
                                    <th className="px-5 py-3 reedos-eyebrow">GSTIN</th>
                                    <th className="px-5 py-3 reedos-eyebrow">State</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Contact</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Owner</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Added</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody data-testid="clients-table">
                                {visibleItems.map((c) => (
                                    <tr key={c.id} className={`border-t border-slate-100 reedos-row-hover ${c.deleted_at ? "opacity-60 bg-slate-50/60" : ""}`} data-testid={`client-row-${c.id}`}>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Link to={`/clients/${c.id}/ledger`} className="font-semibold text-slate-900 hover:underline" data-testid={`client-name-link-${c.id}`}>
                                                    {c.name}
                                                </Link>
                                                {c.deleted_at && <ArchivedBadge testid={`client-archived-badge-${c.id}`} />}
                                            </div>
                                            {/* Iter 49.0 — natural key, surfaced inline under the
                                                name for operator socialisation with external systems.
                                                Monospace + muted so it stays visually subordinate. */}
                                            {c.client_code && (
                                                <div
                                                    className="text-[10px] font-mono text-slate-400 mt-0.5 flex items-center"
                                                    title="Client code (natural key — used for CSV import & external system references)"
                                                >
                                                    <span data-testid={`client-code-${c.id}`}>{c.client_code}</span>
                                                    <CopyCodeButton
                                                        code={c.client_code}
                                                        label="client code"
                                                        testid={`client-code-copy-${c.id}`}
                                                    />
                                                </div>
                                            )}
                                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                {c.segment && (
                                                    <span className="inline-flex items-center rounded px-1.5 py-0 text-[9px] font-bold border bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`client-segment-chip-${c.id}`}>
                                                        {c.segment}
                                                    </span>
                                                )}
                                                {/* Iter 43.1 — International / GST-off visibility chip.
                                                    Tooltip surfaces the rationale so operators don't
                                                    accidentally apply GST during month-end runs. */}
                                                {c.is_international && (
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded px-1.5 py-0 text-[9px] font-bold border bg-amber-50 text-amber-700 border-amber-200"
                                                        title={c.country ? `${c.country} · GST not applicable (export billing)` : "GST not applicable / export billing"}
                                                        data-testid={`client-international-chip-${c.id}`}
                                                    >
                                                        <Globe size={10} weight="fill" /> International
                                                    </span>
                                                )}
                                            </div>
                                            {c.address && (
                                                <div className="text-xs text-slate-500 mt-0.5 flex items-start gap-1">
                                                    <MapPin size={12} className="mt-0.5 shrink-0" />
                                                    <span className="line-clamp-2">{c.address}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs text-slate-700">{c.gstin || "—"}</td>
                                        <td className="px-5 py-3 text-slate-700">{c.state_code || "—"}</td>
                                        <td className="px-5 py-3">
                                            <div className="text-slate-900">{c.contact_name || "—"}</div>
                                            {(c.contact_email || c.contact_phone) && (
                                                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                                    {c.contact_email && (
                                                        <span className="inline-flex items-center gap-1"><Envelope size={11} />{c.contact_email}</span>
                                                    )}
                                                    {c.contact_phone && (
                                                        <span className="inline-flex items-center gap-1 font-mono"><Phone size={11} />{c.contact_phone}</span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            <OwnerCell
                                                owner={c.owner}
                                                testId={`client-owner-${c.id}`}
                                                onReassignClick={isSuperAdmin ? () => setReassignTarget(c) : undefined}
                                            />
                                        </td>
                                        <td className="px-5 py-3 text-right text-xs text-slate-500">{formatDate(c.created_at)}</td>
                                        <td className="px-5 py-3 text-right">
                                            <div className="inline-flex items-center gap-1.5 justify-end">
                                                <RoleGuard permission="client:write">
                                                    {!c.deleted_at && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-slate-600 hover:text-slate-900"
                                                            onClick={() => setEditTarget(c)}
                                                            data-testid={`client-edit-btn-${c.id}`}
                                                            title="Edit client"
                                                        >
                                                            <PencilSimple size={13} />
                                                        </Button>
                                                    )}
                                                </RoleGuard>
                                                {isPrimaryAdmin && (
                                                    c.deleted_at
                                                        ? <UnarchiveRowButton onClick={() => archive.openUnarchive(c)} testid={`client-unarchive-${c.id}`} />
                                                        : <ArchiveRowButton onClick={() => archive.openArchive(c)} testid={`client-archive-${c.id}`} />
                                                )}
                                                <Link to={`/clients/${c.id}/ledger`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900" data-testid={`client-ledger-link-${c.id}`}>
                                                    Ledger <ArrowRight size={11} weight="bold" />
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Controlled create dialog */}
            <EditBillingDetailsDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                client={null}
                onSaved={load}
            />
            {/* Controlled edit dialog (one instance, opened by setEditTarget) */}
            <EditBillingDetailsDialog
                open={Boolean(editTarget)}
                onOpenChange={(o) => { if (!o) setEditTarget(null); }}
                client={editTarget}
                onSaved={load}
            />
            {/* Reassign-owner dialog — super_admin only */}
            <ReassignOwnerDialog
                open={Boolean(reassignTarget)}
                onOpenChange={(o) => { if (!o) setReassignTarget(null); }}
                entityLabel="Client"
                entityName={reassignTarget?.name}
                currentOwner={reassignTarget?.owner}
                reassignUrl={reassignTarget ? `/billing/client/${reassignTarget.id}/owner` : ""}
                cascadePreviewUrl={reassignTarget ? `/billing/client/${reassignTarget.id}/cascade-preview` : null}
                onSuccess={load}
                testIdPrefix="client-reassign"
            />
            {/* Iter 51.1 — Archive/Unarchive confirm dialog */}
            {archive.dialog}
        </div>
    );
}
